import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Modal } from '../components/ui';
import type { ResourceSummary } from '../types';

/**
 * Hesabın kendi proxy'si üzerinden açılan gerçek tarayıcı.
 *
 * Buradaki mesele kolaylık değil: taze parola girişi Instagram'ın checkpoint koyduğu
 * olay, ve doğrulama kodunu ya da güvenlik formunu hiçbir parola cevaplayamaz. Bir insan
 * cevaplayabilir — bu pencere onun yeri. Oluşan oturum panelden hiç geçmeden şifreli
 * depoya yazılıyor, yani kopyala-yapıştır adımı da ortadan kalkıyor.
 */
interface Started {
  sessionId: string;
  streamToken: string;
  expiresAt: string;
  streamPath: string;
  proxy: { name: string; safeUrl: string; country: string | null } | null;
}

/** Sunucudaki görüntü alanı. Tıklama koordinatları buna ölçeklenir. */
const VIEW = { width: 1000, height: 760 };

export function AssistedLogin({
  account,
  onClose,
  onDone,
}: {
  account: ResourceSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const [started, setStarted] = useState<Started | null>(null);
  const [url, setUrl] = useState('');
  const [history, setHistory] = useState({ back: false, forward: false });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  // -- oturumu aç ------------------------------------------------------------
  useEffect(() => {
    let iptal = false;
    api
      .post<Started>(`/v1/instagram/accounts/${account.id}/assisted-login`, {})
      .then((value) => {
        if (!iptal) setStarted(value);
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      );
    return () => {
      iptal = true;
    };
  }, [account.id]);

  // -- akışa bağlan ----------------------------------------------------------
  useEffect(() => {
    if (!started) return;
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    const socket = new WebSocket(
      `${scheme}://${location.host}${started.streamPath}?token=${started.streamToken}`,
    );
    socketRef.current = socket;

    socket.onmessage = (event) => {
      const message = JSON.parse(String(event.data)) as
        | { type: 'frame'; data: string }
        | { type: 'url'; url: string; canGoBack: boolean; canGoForward: boolean }
        | { type: 'selection'; text: string }
        | { type: 'closed'; reason: string };
      if (message.type === 'url') {
        setUrl(message.url);
        return setHistory({ back: message.canGoBack, forward: message.canGoForward });
      }
      if (message.type === 'selection') {
        // Uzaktaki seçim operatörün KENDİ panosuna yazılıyor; tarayıcı izin vermezse
        // sessizce geçiliyor, çünkü bu akışın asıl işi giriş yapmak.
        void navigator.clipboard?.writeText(message.text).catch(() => undefined);
        return;
      }
      if (message.type === 'closed') return setError(`Tarayıcı kapandı (${message.reason}).`);

      const canvas = canvasRef.current;
      if (!canvas) return;
      const image = new Image();
      image.onload = () => canvas.getContext('2d')?.drawImage(image, 0, 0, VIEW.width, VIEW.height);
      image.src = `data:image/jpeg;base64,${message.data}`;
    };
    socket.onerror = () => setError('Tarayıcı akışına bağlanılamadı.');

    return () => socket.close();
  }, [started]);

  const send = useCallback((message: unknown) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  }, []);

  /** Ekrandaki nokta -> sunucudaki görüntü alanı. */
  const at = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.round(((event.clientX - box.left) / box.width) * VIEW.width),
      y: Math.round(((event.clientY - box.top) / box.height) * VIEW.height),
    };
  };

  // -- klavye: canvas odaktayken -------------------------------------------
  useEffect(() => {
    const odakli = () => document.activeElement === canvasRef.current;

    const key = (event: KeyboardEvent) => {
      if (!odakli()) return;
      const kisayol = event.ctrlKey || event.metaKey;

      // Yapıştırma tuşu İLETİLMİYOR, bilerek: pano operatörün makinesinde, tarayıcı ise
      // sunucuda. Ctrl+V'yi iletmek sunucunun boş panosunu yapıştırırdı. Tarayıcının
      // kendi paste olayı metni veriyor, o metni ayrıca gönderiyoruz.
      if (kisayol && event.key.toLowerCase() === 'v') return;
      event.preventDefault();
      if (kisayol && event.key.toLowerCase() === 'c' && event.type === 'keydown') {
        return send({ type: 'copy' });
      }

      send({
        type: 'key',
        action: event.type === 'keydown' ? 'down' : 'up',
        key: event.key,
        code: event.code,
        // Chromium'un kendi maskesi: Alt 1, Ctrl 2, Meta 4, Shift 8.
        modifiers:
          (event.altKey ? 1 : 0) |
          (event.ctrlKey ? 2 : 0) |
          (event.metaKey ? 4 : 0) |
          (event.shiftKey ? 8 : 0),
        // Yalnız basılabilir karakterler metin taşır; Backspace/Enter taşımamalı —
        // onlar sanal tuş koduyla iş görüyor.
        ...(event.type === 'keydown' && event.key.length === 1 && !kisayol
          ? { text: event.key }
          : {}),
      });
    };

    const paste = (event: ClipboardEvent) => {
      if (!odakli()) return;
      event.preventDefault();
      const text = event.clipboardData?.getData('text') ?? '';
      if (text) send({ type: 'text', text });
    };

    window.addEventListener('keydown', key);
    window.addEventListener('keyup', key);
    window.addEventListener('paste', paste);
    return () => {
      window.removeEventListener('keydown', key);
      window.removeEventListener('keyup', key);
      window.removeEventListener('paste', paste);
    };
  }, [send]);

  const capture = () => {
    if (!started) return;
    setBusy(true);
    setError(null);
    api
      .post<{ imported: boolean; url: string; cookieNames?: string[] }>(
        `/v1/instagram/accounts/${account.id}/assisted-login/${started.sessionId}/capture`,
        {},
      )
      .then((value) => {
        if (value.imported) {
          setResult('Oturum alındı ve şifreli olarak kaydedildi.');
          onDone();
        } else {
          // Hangi çerezlerin BULUNDUĞUNU söylemek, "olmadı" demekten farklı bir şey:
          // csrftoken var ama sessionid yoksa giriş tamamlanmamıştır; hiç çerez yoksa
          // sayfa Instagram bile değildir.
          const bulunan = value.cookieNames?.length
            ? `Bu sayfada bulunan çerezler: ${value.cookieNames.join(', ')}.`
            : 'Bu sayfada hiç çerez yok.';
          setError(
            `Oturum çerezi (sessionid) bulunamadı — giriş henüz tamamlanmamış olabilir. ${bulunan} ` +
              `Sayfa: ${value.url}. Girişi bitirip ana akışı gördüğünde tekrar dene.`,
          );
        }
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      )
      .finally(() => setBusy(false));
  };

  const cancel = () => {
    if (started) {
      void api.del(
        `/v1/instagram/accounts/${account.id}/assisted-login/${started.sessionId}`,
      );
    }
    onClose();
  };

  return (
    <Modal
      title={`Tarayıcıdan giriş — ${account.name}`}
      submitLabel={busy ? 'Alınıyor…' : 'Oturumu kaydet'}
      onClose={cancel}
      onSubmit={capture}
      busy={!started || busy}
      error={error}
      wide
    >
      {started?.proxy && (
        <p className="note">
          Çıkış: <strong>{started.proxy.safeUrl}</strong>
          {started.proxy.country ? ` (${started.proxy.country.toUpperCase()})` : ''} —{' '}
          {started.proxy.name}. Oturum bu adresten kurulacak, tarama da buradan çıkacak.
        </p>
      )}
      {result && <p className="note">{result}</p>}
      {!started && !error && <p className="note">Tarayıcı açılıyor…</p>}

      <div className="assisted-bar">
        <button
          type="button"
          className="ghost tiny"
          title="Geri"
          disabled={!history.back}
          onClick={() => send({ type: 'nav', action: 'back' })}
        >
          ←
        </button>
        <button
          type="button"
          className="ghost tiny"
          title="İleri"
          disabled={!history.forward}
          onClick={() => send({ type: 'nav', action: 'forward' })}
        >
          →
        </button>
        <button
          type="button"
          className="ghost tiny"
          title="Yenile"
          onClick={() => send({ type: 'nav', action: 'reload' })}
        >
          ⟳
        </button>
        <span className="assisted-url" title={url}>
          {url || '—'}
        </span>
      </div>

      <canvas
        ref={canvasRef}
        width={VIEW.width}
        height={VIEW.height}
        tabIndex={0}
        className="assisted-canvas"
        onMouseDown={(e) => {
          e.currentTarget.focus();
          send({ type: 'mouse', action: 'down', ...at(e), button: 'left', clickCount: e.detail || 1 });
        }}
        onMouseUp={(e) =>
          send({ type: 'mouse', action: 'up', ...at(e), button: 'left', clickCount: e.detail || 1 })
        }
        onMouseMove={(e) => send({ type: 'mouse', action: 'move', ...at(e) })}
        onWheel={(e) => send({ type: 'wheel', ...at(e), deltaX: e.deltaX, deltaY: e.deltaY })}
      />

      <p className="note">
        Tuvale tıkla, sonra klavye oraya yazar. Instagram ne isterse burada yap — parola,
        doğrulama kodu, güvenlik formu. Girişi bitirip ana sayfayı gördüğünde{' '}
        <strong>Oturumu kaydet</strong>'e bas. Rab-bit senin adına hiçbir doğrulamayı atlamaz;
        geçen sensin, sistem yalnızca oluşan oturumu saklar.
      </p>
    </Modal>
  );
}
